import React, { useState, useRef, useEffect } from 'react';

// COMPONENTS
//import { FixedSizeList as List } from "react-window";
import IHRCheckInReportGraphSegment from '../../ui/reports/IHRCheckInReportGraphSegment';
import IconButton from '../../ui/buttons/IconButton/IconButton';
import LoadingSpinner from '../../ui/modals/Spinners/LoadingSpinner';
import {
  BPCheckInAverage,
  BPCheckinBarGraphType,
} from '../../ui/charts/WeeklyBPBarChart';
import ReportHRGraph, {
  ReportHRGraphData,
} from '../../ui/charts/ReportHRGraph';
import BarChartGraph from '../../ui/charts/WeeklyBPBarChart';
import CheckInTagGroup from '../../ui/display/CheckInTagGroup';
import ScrollableReportHRGraph from '../../ui/charts/ScrollableHRGraph';
import { Virtuoso } from 'react-virtuoso';

// MODULES
import { Helmet } from 'react-helmet-async';

// HOOKS
import { useParams, useSearchParams } from 'react-router-dom';
import useBrowserClass from '../../../hooks/environment/useBrowserClass';
import { useAuthContext } from '../../../hooks/context/AuthContext';
import { useResizeDetector } from 'react-resize-detector';

// TYPES
import {
  IBPCheckin,
  IDetailedCheckin,
  IPPGCheckin,
} from '../../../hooks/apis/apiResponseTypes';

// ICONS + IMAGES
import {
  CopyrightIcon,
  PrintIcon,
  UserIcon,
  DocumentNotFoundIcon,
  TagsIcon,
  NotesIcon,
} from '../../ui/iconify/icons';
import ihrLogo from '../../../static/img/IHR-by-Bodymatter_Logo_Black.png';

// STYLES
import './ShareReport.scss';

// UTILS
import {
  formatIsoForDisplay,
  formatUtcDateStringInZone,
  formatUtcStringInZone,
  parseUtcDate,
} from '../../../utils/dateTime';

// FAKE DATA
//import { exampleResponseWithBP } from "../DummyRoutes/exampleResponseWithBp";

//const ROW_HEIGHT = 420;

interface IReportDisplayProps {
  includeHeader?: boolean;
  reportId: string;
  hrCheckIns: IPPGCheckin[];
  detailedCheckIns: IDetailedCheckin[];
  bpCheckIns: IBPCheckin[];
  reportContainsNoHRCheckins: boolean;
  reportContainsNoBPCheckins: boolean;
  inPrintMode: boolean;
  containerWidthOverride: null | number;
  fromTimestampUtc: string;
  toTimestampUtc: string;
  createdAtTimestampUtc: string;
  reportStartDate: string;
  reportEndDate: string;
  onPrint: () => void;
}

interface IShareReportProps {
  className?: string;
}

const MS_PER_DAY = 86_400_000;

function getOffsetMinutesFromIso(iso: string): number | null {
  if (!iso) return null;
  if (iso.endsWith('Z')) return 0;
  const m = iso.match(/([+-])(\d{2}):?(\d{2})$/);
  if (!m) return null;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
}

function formatYMDZoned(d: Date, offsetMin: number): string {
  const local = new Date(d.getTime() + offsetMin * 60_000);
  const y = local.getUTCFullYear();
  const m = local.getUTCMonth() + 1;
  const day = local.getUTCDate();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${y}-${pad(m)}-${pad(day)}`;
}

/** Start-of-week (Sunday=0) at 00:00 in the zone defined by offsetMin. */
function startOfWeekZoned(
  d: Date,
  offsetMin: number,
  weekStartsOn: 0 | 1 = 0,
): Date {
  const local = new Date(d.getTime() + offsetMin * 60_000);
  const y = local.getUTCFullYear();
  const m = local.getUTCMonth();
  const day = local.getUTCDate();
  const dow = local.getUTCDay();
  const delta = (dow - weekStartsOn + 7) % 7;

  // local midnight (at zone), expressed on UTC timeline:
  const localMidnightUtcMs = Date.UTC(y, m, day) - offsetMin * 60_000;
  return new Date(localMidnightUtcMs - delta * MS_PER_DAY);
}

// const extractRestingHRFromPPGCheckins = (
//     sortedCheckins: IPPGCheckin[],
//     startDateStr: string,
//     endDateStr: string,
// ): BPCheckInAverage[] => {
//     const weeks = new Map<string, IPPGCheckin[]>();
//     for (const checkin of sortedCheckins) {
//         const isResting = checkin.userTags?.some((tag) =>
//             tag.toLowerCase().includes("resting"),
//         );
//         if (!isResting) continue;

//         const date = parseUtcDate(checkin.timestampUtc);
//         const weekStart = getStartOfWeekString(date);
//         if (!weeks.has(weekStart)) {
//             weeks.set(weekStart, []);
//         }
//         weeks.get(weekStart)!.push(checkin);
//     }

//     const startDate = parseUtcDate(startDateStr);
//     const endDate = parseUtcDate(endDateStr);

//     const firstWeekStart = parseUtcDate(getStartOfWeekString(startDate));
//     const lastWeekStart = parseUtcDate(getStartOfWeekString(endDate));

//     const result: BPCheckInAverage[] = [];
//     const current = new Date(firstWeekStart);

//     while (current <= lastWeekStart) {
//         const rawWeekStart = new Date(current);
//         const rawWeekEnd = new Date(current);
//         rawWeekEnd.setUTCDate(rawWeekEnd.getUTCDate() + 6);

//         const weekStart = formatDate(
//             rawWeekStart.getTime() === firstWeekStart.getTime()
//                 ? startDate
//                 : rawWeekStart,
//         );

//         const weekEnd = formatDate(
//             rawWeekStart.getTime() === lastWeekStart.getTime()
//                 ? endDate
//                 : rawWeekEnd,
//         );

//         const lookupKey = formatDate(rawWeekStart);
//         const checkins = weeks.get(lookupKey) ?? [];

//         if (checkins.length > 0) {
//             const total = checkins.reduce(
//                 (sum, checkin) => sum + checkin.value,
//                 0,
//             );
//             const averageHR = total / checkins.length;

//             result.push({
//                 systolicValue: 0,
//                 diastolicValue: 0,
//                 heartRateValue: averageHR,
//                 weekStart,
//                 weekEnd,
//             });
//         } else {
//             result.push({
//                 systolicValue: 0,
//                 diastolicValue: 0,
//                 heartRateValue: 0,
//                 weekStart,
//                 weekEnd,
//             });
//         }

//         current.setUTCDate(current.getUTCDate() + 7);
//     }

//     return result;
// };

// const extractRestingHRFromPPGCheckins = (
//     sortedCheckins: IPPGCheckin[],
//     startDateStr: string,
//     endDateStr: string,
// ): BPCheckInAverage[] => {
//     // Choose a single "report zone" from inputs (prefer start/end; fallback to first check-in)
//     let reportOffset =
//         getOffsetMinutesFromIso(startDateStr) ??
//         getOffsetMinutesFromIso(endDateStr) ??
//         getOffsetMinutesFromIso(
//             sortedCheckins[0]?.checkinDateTimeStamp ?? "",
//         ) ??
//         0;

//     const weeks = new Map<string, IPPGCheckin[]>();

//     for (const checkin of sortedCheckins) {
//         const isResting = checkin.userTags?.some((t) =>
//             t.toLowerCase().includes("resting"),
//         );
//         if (!isResting) continue;

//         const dt = new Date(checkin.checkinDateTimeStamp); // respects embedded offset
//         const weekStartDate = startOfWeekZoned(dt, reportOffset);
//         const weekKey = formatYMDZoned(weekStartDate, reportOffset);

//         if (!weeks.has(weekKey)) weeks.set(weekKey, []);
//         weeks.get(weekKey)!.push(checkin);
//     }

//     const startDate = new Date(startDateStr);
//     const endDate = new Date(endDateStr);

//     const firstWeekStart = startOfWeekZoned(startDate, reportOffset);
//     const lastWeekStart = startOfWeekZoned(endDate, reportOffset);

//     const result: BPCheckInAverage[] = [];
//     let current = new Date(firstWeekStart);

//     while (current.getTime() <= lastWeekStart.getTime()) {
//         const rawWeekStart = new Date(current);
//         const rawWeekEnd = new Date(rawWeekStart.getTime() + 6 * MS_PER_DAY);

//         const isFirst = rawWeekStart.getTime() === firstWeekStart.getTime();
//         const isLast = rawWeekStart.getTime() === lastWeekStart.getTime();

//         const weekStart = formatYMDZoned(
//             isFirst ? startDate : rawWeekStart,
//             reportOffset,
//         );
//         const weekEnd = formatYMDZoned(
//             isLast ? endDate : rawWeekEnd,
//             reportOffset,
//         );

//         const lookupKey = formatYMDZoned(rawWeekStart, reportOffset);
//         const checkins = weeks.get(lookupKey) ?? [];

//         if (checkins.length > 0) {
//             const total = checkins.reduce((sum, c) => sum + c.value, 0);
//             const averageHR = total / checkins.length;

//             result.push({
//                 systolicValue: 0,
//                 diastolicValue: 0,
//                 heartRateValue: averageHR,
//                 weekStart,
//                 weekEnd,
//             });
//         } else {
//             result.push({
//                 systolicValue: 0,
//                 diastolicValue: 0,
//                 heartRateValue: 0,
//                 weekStart,
//                 weekEnd,
//             });
//         }

//         // advance exactly one week on the UTC timeline corresponding to the report zone
//         current = new Date(current.getTime() + 7 * MS_PER_DAY);
//     }

//     return result;
// };

function parseStoredIso(iso: string): { baseEpoch: number; offsetMin: number } {
  const zoneMatch = iso.match(/([Zz]|[+\-]\d{2}:?\d{2})$/);
  const zone = zoneMatch?.[1] ?? 'Z';
  let offsetMin = 0;
  if (zone.toUpperCase() !== 'Z') {
    const m = zone.match(/^([+\-])(\d{2}):?(\d{2})$/);
    if (!m) throw new Error(`Invalid ISO offset in ${iso}`);
    const sign = m[1] === '-' ? -1 : 1;
    offsetMin = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
  }
  const base = iso.replace(/([Zz]|[+\-]\d{2}:?\d{2})$/, '');
  const baseEpoch = Date.parse(`${base}Z`);
  if (Number.isNaN(baseEpoch)) throw new Error(`Invalid ISO ${iso}`);
  return { baseEpoch, offsetMin };
}

const extractRestingHRFromPPGCheckins = (
  sortedCheckins: IPPGCheckin[],
  startDateStr: string,
  endDateStr: string,
): BPCheckInAverage[] => {
  // Report zone for labeling/iteration
  const reportOffset =
    getOffsetMinutesFromIso(startDateStr) ??
    getOffsetMinutesFromIso(endDateStr) ??
    getOffsetMinutesFromIso(sortedCheckins[0]?.checkinDateTimeStamp ?? '') ??
    0;

  const weeks = new Map<string, IPPGCheckin[]>();

  for (const checkin of sortedCheckins) {
    const isResting = checkin.userTags?.some((t) =>
      t.toLowerCase().includes('resting'),
    );
    if (!isResting) continue;

    // 1) Read the check-in using stored-offset semantics
    const { baseEpoch, offsetMin: checkinOffset } = parseStoredIso(
      checkin.checkinDateTimeStamp,
    );

    // 2) Compute local week start (Sunday) in the check-in's own zone,represented on the UTC timeline.
    //    /(Equivalent to startOfWeekZoned but fed with baseEpoch + stored offset)
    const local = new Date(baseEpoch + checkinOffset * 60_000);
    const y = local.getUTCFullYear();
    const m = local.getUTCMonth();
    const d = local.getUTCDate();
    const dow = local.getUTCDay(); // 0=Sun
    const localMidnightUtcMs = Date.UTC(y, m, d) - checkinOffset * 60_000;
    const weekStartUtc = new Date(localMidnightUtcMs - dow * MS_PER_DAY);

    // 3) Bucket key: how that week-start looks in the report zone
    const weekKey = formatYMDZoned(weekStartUtc, reportOffset);
    if (!weeks.has(weekKey)) weeks.set(weekKey, []);
    weeks.get(weekKey)!.push(checkin);
  }

  // Build the labeled week frames in the report zone
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  const firstWeekStart = startOfWeekZoned(startDate, reportOffset);
  const lastWeekStart = startOfWeekZoned(endDate, reportOffset);

  const result: BPCheckInAverage[] = [];
  let current = new Date(firstWeekStart);

  while (current.getTime() <= lastWeekStart.getTime()) {
    const rawWeekStart = new Date(current);
    const rawWeekEnd = new Date(rawWeekStart.getTime() + 6 * MS_PER_DAY);

    const isFirst = rawWeekStart.getTime() === firstWeekStart.getTime();
    const isLast = rawWeekStart.getTime() === lastWeekStart.getTime();

    const weekStart = formatYMDZoned(
      isFirst ? startDate : rawWeekStart,
      reportOffset,
    );
    const weekEnd = formatYMDZoned(isLast ? endDate : rawWeekEnd, reportOffset);

    const lookupKey = formatYMDZoned(rawWeekStart, reportOffset);
    const checkins = weeks.get(lookupKey) ?? [];

    const heartRateValue =
      checkins.length > 0
        ? checkins.reduce((sum, c) => sum + c.value, 0) / checkins.length
        : 0;

    result.push({
      systolicValue: 0,
      diastolicValue: 0,
      heartRateValue,
      weekStart,
      weekEnd,
    });

    current = new Date(current.getTime() + 7 * MS_PER_DAY);
  }

  return result;
};

// const extractBpDataFromCheckins = (
//     sortedCheckins: IBPCheckin[],
//     startDateStr: string,
//     endDateStr: string,
// ): BPCheckInAverage[] => {
//     const weeks = new Map<string, IBPCheckin[]>();

//     for (const checkin of sortedCheckins) {
//         //const date = new Date(checkin.timestampUtc);
//         const date = parseUtcDate(checkin.timestampUtc);
//         const weekStart = getStartOfWeekString(date);
//         if (!weeks.has(weekStart)) {
//             weeks.set(weekStart, []);
//         }
//         weeks.get(weekStart)!.push(checkin);
//     }

//     // const startDate = new Date(startDateStr);
//     // const endDate = new Date(endDateStr);
//     const startDate = parseUtcDate(startDateStr);
//     const endDate = parseUtcDate(endDateStr);

//     // const firstWeekStart = new Date(getStartOfWeekString(startDate));
//     // const lastWeekStart = new Date(getStartOfWeekString(endDate));
//     const firstWeekStart = parseUtcDate(getStartOfWeekString(startDate));
//     const lastWeekStart = parseUtcDate(getStartOfWeekString(endDate));

//     const result: BPCheckInAverage[] = [];
//     const current = new Date(firstWeekStart);

//     while (current <= lastWeekStart) {
//         // const rawWeekStart = new Date(current);
//         // const rawWeekEnd = new Date(current);
//         const rawWeekStart = new Date(current);
//         const rawWeekEnd = new Date(current);
//         rawWeekEnd.setUTCDate(rawWeekEnd.getUTCDate() + 6);

//         const weekStart = formatDate(
//             rawWeekStart.getTime() === firstWeekStart.getTime()
//                 ? startDate
//                 : rawWeekStart,
//         );

//         const weekEnd = formatDate(
//             rawWeekStart.getTime() === lastWeekStart.getTime()
//                 ? endDate
//                 : rawWeekEnd,
//         );

//         const lookupKey = formatDate(rawWeekStart);
//         const checkins = weeks.get(lookupKey) ?? [];
//         let hrCount = 0;

//         if (checkins.length > 0) {
//             const total = checkins.reduce(
//                 (acc, cur) => {
//                     acc.systolicValue += cur.systolicValue;
//                     acc.diastolicValue += cur.diastolicValue;
//                     if (
//                         cur.userTags.some((tag) =>
//                             tag.toLowerCase().includes("resting"),
//                         )
//                     ) {
//                         hrCount++;
//                         acc.heartRateValue += cur.heartRateValue;
//                     }

//                     return acc;
//                 },
//                 { systolicValue: 0, diastolicValue: 0, heartRateValue: 0 },
//             );
//             const count = checkins.length;

//             result.push({
//                 systolicValue: total.systolicValue / count,
//                 diastolicValue: total.diastolicValue / count,
//                 heartRateValue:
//                     hrCount !== 0 ? total.heartRateValue / hrCount : 0,
//                 weekStart,
//                 weekEnd,
//             });
//         } else {
//             result.push({
//                 systolicValue: 0,
//                 diastolicValue: 0,
//                 heartRateValue: 0,
//                 weekStart,
//                 weekEnd,
//             });
//         }

//         current.setUTCDate(current.getUTCDate() + 7);
//     }

//     return result;
// };

const extractBpDataFromCheckins = (
  sortedCheckins: IBPCheckin[],
  startDateStr: string,
  endDateStr: string,
): BPCheckInAverage[] => {
  // Report zone used for week labels & iteration
  const reportOffset =
    getOffsetMinutesFromIso(startDateStr) ??
    getOffsetMinutesFromIso(endDateStr) ??
    getOffsetMinutesFromIso(sortedCheckins[0]?.checkinDateTimeStamp ?? '') ??
    0;

  // Group by week: each check-in uses its own stored offset for local week,
  // then we label that week using the report zone for consistent keys.
  const weeks = new Map<string, IBPCheckin[]>();

  for (const checkin of sortedCheckins) {
    const { baseEpoch, offsetMin: chkOffset } = parseStoredIso(
      checkin.checkinDateTimeStamp,
    );
    const localInstant = new Date(baseEpoch + chkOffset * 60_000);
    const weekStartUtc = startOfWeekZoned(localInstant, chkOffset); // week boundary in check-in's zone
    const weekKey = formatYMDZoned(weekStartUtc, reportOffset); // label in report zone

    if (!weeks.has(weekKey)) weeks.set(weekKey, []);
    weeks.get(weekKey)!.push(checkin);
  }

  // Build week frames in the report zone
  const startInstant = new Date(startDateStr + 'Z');
  const endInstant = new Date(endDateStr + 'Z');
  const firstWeekStart = startOfWeekZoned(startInstant, reportOffset);
  const lastWeekStart = startOfWeekZoned(endInstant, reportOffset);

  const result: BPCheckInAverage[] = [];
  let current = new Date(firstWeekStart);

  while (current.getTime() <= lastWeekStart.getTime()) {
    const rawWeekStart = new Date(current);
    const rawWeekEnd = new Date(current.getTime() + 6 * MS_PER_DAY);

    const isFirst = rawWeekStart.getTime() === firstWeekStart.getTime();
    const isLast = rawWeekStart.getTime() === lastWeekStart.getTime();

    const weekStart = formatYMDZoned(
      isFirst ? startInstant : rawWeekStart,
      reportOffset,
    );
    const weekEnd = formatYMDZoned(
      isLast ? endInstant : rawWeekEnd,
      reportOffset,
    );

    const lookupKey = formatYMDZoned(rawWeekStart, reportOffset);
    const checkins = weeks.get(lookupKey) ?? [];

    if (checkins.length > 0) {
      let sumSys = 0;
      let sumDia = 0;
      let sumHR = 0;
      let hrCount = 0;

      for (const cur of checkins) {
        sumSys += cur.systolicValue;
        sumDia += cur.diastolicValue;
        if (cur.userTags?.some((t) => t.toLowerCase().includes('resting'))) {
          hrCount++;
          sumHR += cur.heartRateValue;
        }
      }

      result.push({
        systolicValue: sumSys / checkins.length,
        diastolicValue: sumDia / checkins.length,
        heartRateValue: hrCount ? sumHR / hrCount : 0,
        weekStart,
        weekEnd,
      });
    } else {
      result.push({
        systolicValue: 0,
        diastolicValue: 0,
        heartRateValue: 0,
        weekStart,
        weekEnd,
      });
    }

    // advance exactly one week on UTC timeline
    current = new Date(current.getTime() + 7 * MS_PER_DAY);
  }

  return result;
};

function getStartOfWeekString(date: Date): string {
  const localDay = date.getDay(); // uses local time
  const offset = -localDay;
  const start = new Date(date);
  start.setDate(start.getDate() + offset);
  start.setHours(0, 0, 0, 0); // normalize
  return formatDate(start);
}

function formatDate(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// const extractHrDataFromCheckins = (
//     sortedCheckins: IPPGCheckin[],
//     reportStartDate: string, //e.g. 2124-11-26T21:46:15.000
//     reportEndDate: string, //e.g. 2124-11-26T21:46:15.000
// ): ReportHRGraphData[] => {
//     if (!sortedCheckins.length) return [];

//     const first = new Date(reportStartDate + "Z");
//     const startDate = new Date(
//         Date.UTC(
//             first.getUTCFullYear(),
//             first.getUTCMonth(),
//             first.getUTCDate(),
//         ),
//     );

//     const last = new Date(reportEndDate + "Z");
//     const endDate = new Date(
//         Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate()),
//     );

//     // (TODO VERIFY) -- normalize to UTC midnight
//     let current = new Date(
//         Date.UTC(
//             startDate.getUTCFullYear(),
//             startDate.getUTCMonth(),
//             startDate.getUTCDate(),
//         ),
//     );

//     const end = new Date(
//         Date.UTC(
//             endDate.getUTCFullYear(),
//             endDate.getUTCMonth(),
//             endDate.getUTCDate(),
//         ),
//     );
//     // build map of dateKey -> [checkins]
//     const grouped = new Map<string, IPPGCheckin[]>();
//     sortedCheckins.forEach((checkin) => {
//         const key = checkin.timestampUtc.split("T")[0]; // e.g. "2025-05-30"
//         if (!grouped.has(key)) grouped.set(key, []);
//         grouped.get(key)!.push(checkin);
//     });

//     const result: ReportHRGraphData[] = [];

//     while (current <= end) {
//         //const isoDate = new Date(current).toISOString().split("T")[0]; // UTC day key
//         const isoDate = current.toISOString().split("T")[0];
//         const checkins = grouped.get(isoDate) ?? [];

//         if (!checkins.length) {
//             result.push({
//                 date: `${isoDate}T01:00:00.000`,
//                 high: 0,
//                 low: 0,
//             });
//         } else {
//             let high = -Infinity;
//             let low = Infinity;
//             checkins.forEach((c) => {
//                 if (c.value > high || high === 0) high = c.value;
//                 if (c.value < low || low === 0) low = c.value;
//             });

//             result.push({
//                 date: `${isoDate}T01:00:00.000`,
//                 high,
//                 low,
//             });
//         }

//         //move to next UTC day
//         current.setUTCDate(current.getUTCDate() + 1);
//     }

//     console.log("RESSSS", result);
//     return result;
// };

const extractHrDataFromCheckins = (
  sortedCheckins: IPPGCheckin[],
  reportStartDate: string, // e.g. "2124-11-26T21:46:15.000"
  reportEndDate: string, // e.g. "2124-11-26T21:46:15.000"
): ReportHRGraphData[] => {
  if (!sortedCheckins.length) return [];

  const MS_PER_DAY = 86_400_000;

  // ---- helpers (stored-offset semantics) -------------------------------
  const parseStoredIso = (
    iso: string,
  ): { baseEpoch: number; offsetMin: number } => {
    const zoneMatch = iso.match(/([Zz]|[+\-]\d{2}:?\d{2})$/);
    const zone = zoneMatch?.[1] ?? 'Z';
    let offsetMin = 0;
    if (zone.toUpperCase() !== 'Z') {
      const m = zone.match(/^([+\-])(\d{2}):?(\d{2})$/);
      if (!m) throw new Error(`Invalid ISO offset in ${iso}`);
      const sign = m[1] === '-' ? -1 : 1;
      offsetMin = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
    }
    const base = iso.replace(/([Zz]|[+\-]\d{2}:?\d{2})$/, '');
    const baseEpoch = Date.parse(`${base}Z`);
    if (Number.isNaN(baseEpoch)) throw new Error(`Invalid ISO ${iso}`);
    return { baseEpoch, offsetMin };
  };

  const startOfDayZoned = (d: Date, offsetMin: number): Date => {
    const local = new Date(d.getTime() + offsetMin * 60_000);
    const y = local.getUTCFullYear();
    const m = local.getUTCMonth();
    const day = local.getUTCDate();
    // local midnight (00:00) expressed on UTC timeline
    const localMidnightUtcMs = Date.UTC(y, m, day) - offsetMin * 60_000;
    return new Date(localMidnightUtcMs);
  };

  const formatYMDZoned = (d: Date, offsetMin: number): string => {
    const local = new Date(d.getTime() + offsetMin * 60_000);
    const y = local.getUTCFullYear();
    const m = local.getUTCMonth() + 1;
    const day = local.getUTCDate();
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${y}-${pad(m)}-${pad(day)}`;
  };

  const getOffsetMinutesFromIso = (iso: string): number | null => {
    const m = iso?.match(/([Zz]|[+\-]\d{2}:?\d{2})$/);
    if (!m) return null;
    const z = m[1];
    if (z.toUpperCase() === 'Z') return 0;
    const mm = z.match(/^([+\-])(\d{2}):?(\d{2})$/);
    if (!mm) return null;
    const sign = mm[1] === '-' ? -1 : 1;
    return sign * (parseInt(mm[2], 10) * 60 + parseInt(mm[3], 10));
  };
  // ---------------------------------------------------------------------

  // Report zone used for day labels & iteration
  const reportOffset =
    getOffsetMinutesFromIso(reportStartDate) ??
    getOffsetMinutesFromIso(reportEndDate) ??
    getOffsetMinutesFromIso(sortedCheckins[0].checkinDateTimeStamp) ??
    0;

  // Group check-ins by the *day they belong to in their own stored offset*,
  // keyed as that day's date in the report zone.
  const grouped = new Map<string, IPPGCheckin[]>();
  for (const checkin of sortedCheckins) {
    const { baseEpoch, offsetMin: chkOffset } = parseStoredIso(
      checkin.checkinDateTimeStamp,
    );
    const dayStartUtc = startOfDayZoned(new Date(baseEpoch), chkOffset); // day boundary in chk zone
    const dayKey = formatYMDZoned(dayStartUtc, reportOffset); // label in report zone
    if (!grouped.has(dayKey)) grouped.set(dayKey, []);
    grouped.get(dayKey)!.push(checkin);
  }

  // Build daily frames in the report zone
  const first = new Date(reportStartDate + 'Z');
  const last = new Date(reportEndDate + 'Z');
  let current = startOfDayZoned(first, reportOffset);
  const end = startOfDayZoned(last, reportOffset);

  const result: ReportHRGraphData[] = [];

  while (current.getTime() <= end.getTime()) {
    const isoDate = formatYMDZoned(current, reportOffset); // "YYYY-MM-DD" in report zone
    const checkins = grouped.get(isoDate) ?? [];

    if (!checkins.length) {
      result.push({ date: `${isoDate}T01:00:00.000`, high: 0, low: 0 });
    } else {
      let high = -Infinity;
      let low = Infinity;
      for (const c of checkins) {
        if (c.value > high) high = c.value;
        if (c.value < low) low = c.value;
      }
      result.push({ date: `${isoDate}T01:00:00.000`, high, low });
    }

    // advance exactly one day on UTC timeline
    current = new Date(current.getTime() + MS_PER_DAY);
  }

  return result;
};

const ReportDisplay: React.FC<IReportDisplayProps> = ({
  includeHeader = true,
  reportId,
  hrCheckIns,
  detailedCheckIns,
  bpCheckIns,
  reportContainsNoHRCheckins,
  reportContainsNoBPCheckins,
  inPrintMode,
  containerWidthOverride,
  fromTimestampUtc,
  toTimestampUtc,
  createdAtTimestampUtc,
  reportStartDate,
  reportEndDate,
  onPrint,
}) => {
  const [filteredSortedHRCheckins, setFilteredSortedHRCheckins] = useState<
    IPPGCheckin[]
  >([]);

  const [filteredSortedBPCheckins, setFilteredSortedBPCheckins] = useState<
    BPCheckInAverage[]
  >([]);

  const [
    filteredSortedPPGRestingHRCheckins,
    setFilteredSortedPPGRestingHRCheckins,
  ] = useState<BPCheckInAverage[]>([]);

  const { width, ref } = useResizeDetector();

  useEffect(() => {
    const data = hrCheckIns
      ?.filter(({ timestampUtc }) => {
        // blindly filter invalid checkins to prevent page errors
        return !isNaN(Number(new Date(timestampUtc.split(' ')[0])));
      })
      .sort(
        (a, b) =>
          Number(new Date(b.timestampUtc)) - Number(new Date(a.timestampUtc)),
      );

    const extracted = extractRestingHRFromPPGCheckins(
      data,
      reportStartDate,
      reportEndDate,
    );

    setFilteredSortedPPGRestingHRCheckins(extracted);
    setFilteredSortedHRCheckins(data);
  }, [hrCheckIns, reportEndDate, reportStartDate]);

  useEffect(() => {
    if (bpCheckIns.length !== 0) {
      const fsCheckins = extractBpDataFromCheckins(
        bpCheckIns.sort(
          (a, b) =>
            Number(new Date(b.timestampUtc)) - Number(new Date(a.timestampUtc)),
        ),
        reportStartDate,
        reportEndDate,
      );
      setFilteredSortedBPCheckins(fsCheckins);
    }
  }, [bpCheckIns, reportStartDate, reportEndDate]);

  return (
    <>
      {/* Header */}
      <div className={`reportHeader ${includeHeader ? '' : 'hidden'}`}>
        <div>
          <img src={ihrLogo} alt="IHR by BodyMatter"></img>
        </div>
        <div id="printButtonWrapper">
          <IconButton
            text="Print Report"
            icon={PrintIcon}
            onClick={() => onPrint()}
          />
        </div>
      </div>
      <div className="contentWrapper">
        {/* Sub-Header */}
        <div className="reportSubHeader">
          <h2>Heart Health Report</h2>
          <h6>Created {formatUtcStringInZone(createdAtTimestampUtc)}</h6>
          <h6>
            {formatUtcDateStringInZone(fromTimestampUtc)} -{' '}
            {formatUtcDateStringInZone(toTimestampUtc)}
          </h6>

          <h6 id="reportId">
            <p>{UserIcon}</p>
            <p>Report ID:</p>
            <p>{reportId}</p>
          </h6>
        </div>
        {/* Body */}
        {/* TODO - convert to block element modifier and create section subclass */}
        <h4>Heart Rate Range By Day</h4>
        <div className="reportSection barGraph" ref={ref}>
          <div className="reportDivider" />
          <div>
            {reportContainsNoHRCheckins === true ? (
              <div className="noCheckins">
                <h4>This report contains no heart rate measurements</h4>
              </div>
            ) : inPrintMode ? (
              <ReportHRGraph
                data={extractHrDataFromCheckins(
                  [...filteredSortedHRCheckins].reverse(),
                  reportStartDate,
                  reportEndDate,
                )}
                containerWidth={width ?? 0}
                inPrintMode={inPrintMode}
              />
            ) : (
              <ScrollableReportHRGraph
                data={extractHrDataFromCheckins(
                  [...filteredSortedHRCheckins].reverse(),
                  reportStartDate,
                  reportEndDate,
                )}
                containerWidth={width ?? 0}
                inPrintMode={inPrintMode}
              />
            )}
          </div>
        </div>
        <h4>Resting Heart Rate</h4>
        <div className="reportSection barGraph">
          <div className="reportDivider" />
          {!reportContainsNoHRCheckins ? (
            filteredSortedPPGRestingHRCheckins.some(
              (d) => d.heartRateValue !== 0,
            ) ? (
              <BarChartGraph
                // data={extractBpDataFromCheckins(
                //     exampleResponseWithBP.bp_logged_data
                //         .bp_checkins as any as IBPCheckin[],
                // )}
                data={filteredSortedPPGRestingHRCheckins}
                barColor="#A3A3AD"
                inPrintMode={inPrintMode}
                graphType={BPCheckinBarGraphType.heartRate}
                width={width}
              />
            ) : (
              <div className="noCheckins">
                <h4>This report contains no resting heart rate measurements</h4>
              </div>
            )
          ) : (
            <div className="noCheckins">
              <h4>This report contains no resting heart rate measurements</h4>
            </div>
          )}
        </div>
        <h4>Systolic Blood Pressure</h4>
        <div className="reportSection barGraph">
          <div className="reportDivider" />
          {!reportContainsNoBPCheckins ? (
            <div>
              <BarChartGraph
                // data={extractBpDataFromCheckins(
                //     defaultWithData.bp_logged_data
                //         .bp_checkins as any as IBPCheckin[],
                //     reportStartDate,
                //     reportEndDate,
                // )}
                data={filteredSortedBPCheckins}
                barColor="#DC4E36"
                inPrintMode={inPrintMode}
                graphType={BPCheckinBarGraphType.systolic}
                width={width}
              />
            </div>
          ) : (
            <div className="noCheckins">
              <h4>This report contains no blood pressure measurements</h4>
            </div>
          )}
        </div>
        <h4>Diastolic Blood Pressure</h4>
        <div className="reportSection barGraph">
          <div className="reportDivider" />
          {!reportContainsNoBPCheckins ? (
            <BarChartGraph
              data={filteredSortedBPCheckins}
              barColor="#DC4E36"
              inPrintMode={inPrintMode}
              graphType={BPCheckinBarGraphType.diastolic}
              width={width}
            />
          ) : (
            <div className="noCheckins">
              <h4>This report contains no blood pressure measurements</h4>
            </div>
          )}
        </div>
        <h4>Finger Pulse Measurements</h4>
        <div className="reportSection">
          {/* <div className="reportDivider" /> */}
          <div className="reportSection">
            <div className="reportDivider" />
            {reportContainsNoHRCheckins ? (
              <div className="noCheckins">
                <h4>This report contains no heart rate measurements</h4>
              </div>
            ) : inPrintMode ? (
              // Print mode – render all rows
              filteredSortedHRCheckins.map((c, idx, arr) => {
                return (
                  <div key={idx}>
                    <IHRCheckInReportGraphSegment
                      start={formatIsoForDisplay(c.checkinDateTimeStamp)}
                      bpm={c.value}
                      ppgWaveform={c.ppgWaveform}
                      inPrintMode={inPrintMode}
                      containerWidthOverride={containerWidthOverride}
                      tags={c.userTags}
                      notes={c.userNote}
                    />
                    {idx !== arr.length - 1 && (
                      <div className="reportDivider" />
                    )}
                  </div>
                );
              })
            ) : (
              <Virtuoso
                style={{
                  height: Math.min(5, filteredSortedHRCheckins.length) * 450,
                }} // initial viewport (~5 rows) - may change if need be
                totalCount={filteredSortedHRCheckins.length}
                data={filteredSortedHRCheckins} // typed as IPPGCheckin[]
                overscan={200}
                itemContent={(index, item) => (
                  <div>
                    <IHRCheckInReportGraphSegment
                      start={formatIsoForDisplay(item.checkinDateTimeStamp)}
                      bpm={item.value}
                      ppgWaveform={item.ppgWaveform}
                      inPrintMode={false}
                      containerWidthOverride={null}
                      tags={item.userTags}
                      notes={item.userNote}
                    />
                    {index !== filteredSortedHRCheckins.length - 1 && (
                      <div className="reportDivider" />
                    )}
                  </div>
                )}
              />
            )}
          </div>
        </div>
        {/* TODO - no inline styling */}
        <h4 style={{ marginTop: '1rem' }}>Blood Pressure Measurements</h4>
        <div className="reportSection">
          <div className="reportDivider" />
          <div className="BPCheckinTable">
            {reportContainsNoBPCheckins === true ? (
              <div className="noCheckins">
                <h4>This report contains no blood pressure measurements</h4>
              </div>
            ) : (
              bpCheckIns.map((bpCheckin, idx) => {
                return (
                  <>
                    <div key={idx} className="BPCheckinTable_Row">
                      <div className="BPCheckinTable_Measurements">
                        <div className="BPCheckinTable_Cell">
                          <div className="value">
                            {bpCheckin.systolicValue} mmHg
                          </div>
                          <div className="label">SYSTOLIC</div>
                        </div>
                        <div className="BPCheckinTable_Cell">
                          <div className="value">
                            {bpCheckin.diastolicValue} mmHg
                          </div>
                          <div className="label">DIASTOLIC</div>
                        </div>
                        <div className="BPCheckinTable_Cell">
                          <div className="value">
                            {bpCheckin.heartRateValue} bpm
                          </div>
                          <div className="label">HEART RATE</div>
                        </div>
                      </div>

                      {/* Date below — full width */}
                      <div className="BPCheckinTable_DateRow">
                        {formatUtcStringInZone(bpCheckin.timestampUtc)}
                      </div>
                      <div className="BPCheckinTable_Metadata">
                        <div className="reportMetadataGrid">
                          <div className="reportIcon">{TagsIcon}</div>
                          <div className="tagsWrapper">
                            {bpCheckin.userTags ? (
                              <CheckInTagGroup tags={bpCheckin.userTags} />
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        {/* Notes */}
                        <div className="reportMetadataGrid">
                          <div className="reportIcon">{NotesIcon}</div>
                          <div className="notesText">{bpCheckin.userNote}</div>
                        </div>
                      </div>
                    </div>
                    <div className="reportDivider" />
                  </>
                );
              })
            )}
          </div>
        </div>
        {/* Footer */}
        <div className="reportFooter">
          <p>
            This report was generated using Instant Heart Rate. Do not rely on
            the information in this report for medical advice or diagnosis. This
            report is not a substitute for professional medical care. All
            results are approximations. A doctor should be consulted with any
            health concern. Medications should not be self-adjusted. The Instant
            Heart Rate{' '}
            <a href="https://www.instantheartrate.com/privacy-policy-and-terms-of-use#tos-section">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://www.instantheartrate.com/privacy-policy-and-terms-of-use#privacy-policy-section">
              Privacy Policy
            </a>{' '}
            apply to the use of this report.
          </p>
          <p className="footerCopyright">
            {CopyrightIcon} {new Date().getFullYear()} Bodymatter, Inc.
          </p>
        </div>
      </div>
    </>
  );
};

const ReportNotFound: React.FC = () => {
  return (
    <div className="ReportNotFound">
      <div>{DocumentNotFoundIcon}</div>
      <h3>Report Not Found</h3>
    </div>
  );
};

const ShareReport: React.FC<IShareReportProps> = ({ className = '' }) => {
  const [showLoadingSpinner, setShowLoadingSpinner] = useState<boolean>(true);
  const [reportContainsNoHRCheckins, setReportContainsNoHRCheckins] =
    useState<boolean>(false);

  const [reportContainsNoBPCheckins, setReportContainsNoBPCheckins] =
    useState<boolean>(false);

  const reportQueried = useRef<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    hrCheckIns,
    detailedCheckins,
    bpCheckIns,
    reportNotFound,
    isAuthResolved,
    isLoading,
    createdAtTimestampUtc,
    fromTimestampUtc,
    toTimestampUtc,
    getReport,
  } = useAuthContext();

  // Lets be type safe with this - don't remove the typing even though I'm pretty sure this should be handled by the error element
  const { reportId }: { [paramName: string]: string | undefined } = useParams();

  const [inPrintMode, setInPrintMode] = useState(false);
  const [containerWidthOverride, setContainerWidthOverride] = useState<
    null | number
  >(null);

  useEffect(() => {
    const handleBeforePrint = () => {
      //console.log("Before print");
      setInPrintMode(true);
      setContainerWidthOverride(700); // or whatever logic you need
    };

    const handleAfterPrint = () => {
      //console.log("After print");
      setInPrintMode(false);
      setContainerWidthOverride(null);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // Wait until React has committed the print-mode re-render AND D3 has
  // finished drawing every chart inside the report. We detect "done" by
  // polling the DOM and requiring the segment + SVG counts to stay stable
  // for several ticks. A hard safety timeout prevents hanging.
  const waitForPrintReady = () => {
    return new Promise<void>((resolve) => {
      const contentEl = contentRef.current;
      if (!contentEl) {
        resolve();
        return;
      }

      const MAX_WAIT_MS = 8000;
      const POLL_INTERVAL_MS = 80;
      const REQUIRED_STABLE_TICKS = 3;

      let lastSegmentCount = -1;
      let lastSvgCount = -1;
      let stableTicks = 0;
      const startTime = Date.now();

      const tick = () => {
        if (Date.now() - startTime > MAX_WAIT_MS) {
          resolve();
          return;
        }

        const segmentCount = contentEl.querySelectorAll(
          '.IHRCheckInReportGraphSegment',
        ).length;
        const svgCount = contentEl.querySelectorAll('svg').length;

        const stable =
          segmentCount === lastSegmentCount &&
          svgCount === lastSvgCount &&
          svgCount >= segmentCount;

        if (stable) {
          stableTicks += 1;
          if (stableTicks >= REQUIRED_STABLE_TICKS) {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => resolve()),
            );
            return;
          }
        } else {
          stableTicks = 0;
          lastSegmentCount = segmentCount;
          lastSvgCount = svgCount;
        }

        setTimeout(tick, POLL_INTERVAL_MS);
      };

      requestAnimationFrame(() => requestAnimationFrame(tick));
    });
  };

  // Use the browser's native print pipeline instead of react-to-print's
  // hidden iframe. The iframe approach was clipping the report to one page
  // because its tiny viewport messed up the layout (height: 100vh,
  // overflow:auto, react-virtuoso etc). Native print uses the real document
  // layout and the @media print stylesheet, which produces a correct
  // multi-page output. The beforeprint/afterprint listeners above keep
  // inPrintMode in sync if the user triggers print via Cmd/Ctrl+P directly.
  const [searchParams] = useSearchParams();
  const reactToPrintFn = async () => {
    setInPrintMode(true);
    setContainerWidthOverride(700);

    await waitForPrintReady();

    window.print();
    // afterprint listener resets inPrintMode / containerWidthOverride
  };

  const browserClass = useBrowserClass();

  useEffect(() => {
    // prevent dupe calls in strict mode with reportQueried
    if (reportQueried.current === false && isAuthResolved) {
      reportQueried.current = true;
      const sk = searchParams.get('sk') ?? '';
      setShowLoadingSpinner(true);
      // getReport(reportId ?? "", sk, false); // does not paginate
      getReport(reportId ?? '', sk, true); // paginates
    }

    // ignore exhaustive deps for now
    // eslint-disable-next-line
  }, [isAuthResolved]);

  useEffect(() => {
    setShowLoadingSpinner(isLoading);
    if (reportNotFound === false) {
      if (hrCheckIns.length === 0) {
        setReportContainsNoHRCheckins(true);
      }
      if (bpCheckIns.length === 0) {
        setReportContainsNoBPCheckins(true);
      }
    }

    // ignore exhaustive deps for now
    // eslint-disable-next-line
  }, [isLoading]);

  return (
    <>
      {/* TODO - replace when <Meta /> tag is available in react 19 */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <LoadingSpinner raised={showLoadingSpinner} fullPage={true} />
      <div
        className={`ShareReport ${browserClass} ${className}`}
        ref={contentRef}
      >
        {reportNotFound !== null ? (
          reportNotFound === false ? (
            <ReportDisplay
              reportId={reportId ?? ''}
              hrCheckIns={hrCheckIns}
              detailedCheckIns={detailedCheckins}
              bpCheckIns={bpCheckIns}
              includeHeader={
                searchParams.get('hdr') === undefined ||
                searchParams.get('hdr') !== '0'
              }
              reportContainsNoHRCheckins={reportContainsNoHRCheckins}
              reportContainsNoBPCheckins={reportContainsNoBPCheckins}
              createdAtTimestampUtc={createdAtTimestampUtc ?? ''}
              fromTimestampUtc={fromTimestampUtc ?? ''}
              toTimestampUtc={toTimestampUtc ?? ''}
              inPrintMode={inPrintMode}
              containerWidthOverride={containerWidthOverride}
              reportStartDate={fromTimestampUtc ?? ''}
              reportEndDate={toTimestampUtc ?? ''}
              onPrint={reactToPrintFn}
            />
          ) : (
            <ReportNotFound />
          )
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default ShareReport;
